import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getAuth, signOut } from 'firebase/auth';
import ga from '../services/analytics';
import { preserveUtmParams } from '../utils/utm';
import { getPostStoragePath } from '../utils/onboardingNavigation';
import {
  type StorageProvider,
  type StorageStatus,
  fetchStorageStatus,
  setDefaultStorageProvider,
  continueWithCassetteCloud,
} from '../services/storageService';
import { SketchPageShell, SketchBox, SketchButton } from '../components/sketchy';
import StorageProviderGrid from '../components/storage/StorageProviderGrid';
import CassetteCloudPaywallModal from '../components/CassetteCloudPaywallModal';
import { isEligibleForCassetteProjectStorage } from '../components/storage/storageProviderUi';
import {
  cloudErrorToastMessage,
  isHandledCloudErrorCode,
  parseCloudErrorPayload,
} from '../services/cloudErrorGuide';

function isLinked(status: StorageStatus | null, provider: StorageProvider): boolean {
  return !!status?.linkedProviders?.includes(provider);
}

const LinkStoragePage: React.FC = () => {
  const { user, linkStorage, isNewUser, eligibleForCassetteProjectStorage } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isExpiredReconnect = searchParams.get('reason') === 'expired';
  const isFailedReconnect = searchParams.get('reason') === 'failed';
  const reconnectProvider = searchParams.get('provider') as StorageProvider | null;
  const { t } = useTranslation();
  const [busy, setBusy] = useState<StorageProvider | null>(null);
  const [status, setStatus] = useState<StorageStatus | null>(null);
  const [defaultProvider, setDefaultProvider] = useState<StorageProvider | ''>('');
  const [savingDefault, setSavingDefault] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [showCassettePaywall, setShowCassettePaywall] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const s = await fetchStorageStatus();
      setStatus(s);
      setDefaultProvider(s.defaultStorageProvider || s.storageProvider || '');
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    ga.pageView('/auth/storage', 'Connect Cloud Storage - Cassette');
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    const onLinked = () => {
      void refreshStatus();
    };
    window.addEventListener('cassette-storage-linked', onLinked);
    return () => window.removeEventListener('cassette-storage-linked', onLinked);
  }, [refreshStatus]);

  const linked = status?.linkedProviders ?? [];
  const ccProjectStorageEligible = isEligibleForCassetteProjectStorage(status, eligibleForCassetteProjectStorage);
  const ccLinked = isLinked(status, 'cassette_s3');
  const canContinue =
    linked.some((p) => p !== 'none' && p !== 'cassette_s3') ||
    (defaultProvider === 'cassette_s3' && ccProjectStorageEligible && ccLinked);

  const handleConnect = async (provider: StorageProvider) => {
    setConnectError(null);
    if (provider === 'cassette_s3' && !ccProjectStorageEligible) {
      setShowCassettePaywall(true);
      return;
    }
    setBusy(provider);
    try {
      if (provider === 'cassette_s3') {
        await continueWithCassetteCloud();
        await refreshStatus();
      } else {
        await linkStorage(provider);
      }
    } catch (e) {
      console.error('Storage link failed:', e);
      if (isAxiosError(e)) {
        const raw = e.response?.data;
        const data =
          raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
        const payload = parseCloudErrorPayload(data, e.response?.status);
        if (payload?.code === 'cassette_cloud_not_included') {
          setShowCassettePaywall(true);
          setConnectError(cloudErrorToastMessage(payload, t));
          return;
        }
        if (payload && isHandledCloudErrorCode(payload.code)) {
          setConnectError(cloudErrorToastMessage(payload, t));
          return;
        }
        const code =
          typeof raw === 'object' && raw && 'error' in raw
            ? String((raw as { error: unknown }).error)
            : '';
        if (code === 'account_link_required') {
          setConnectError(t('settings.storage.accountLinkRequired', 'Create an account before linking cloud storage.'));
          return;
        }
      }
      setConnectError(
        e instanceof Error ? e.message : t('settings.storage.connectFailed', 'Could not connect cloud storage.'),
      );
    } finally {
      setBusy(null);
    }
  };

  const handleDefaultChange = async (provider: StorageProvider) => {
    if (provider === 'cassette_s3' && !ccProjectStorageEligible) {
      setShowCassettePaywall(true);
      return;
    }
    setDefaultProvider(provider);
    setSavingDefault(true);
    try {
      if (provider === 'cassette_s3' && !ccLinked) {
        await continueWithCassetteCloud();
      } else {
        await setDefaultStorageProvider(provider);
      }
      await refreshStatus();
    } catch (e) {
      console.error('Set default failed:', e);
      if (isAxiosError(e)) {
        const raw = e.response?.data;
        const data =
          raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
        const payload = parseCloudErrorPayload(data, e.response?.status);
        if (payload?.code === 'cassette_cloud_not_included') {
          setShowCassettePaywall(true);
          setConnectError(cloudErrorToastMessage(payload, t));
          return;
        }
        if (payload && isHandledCloudErrorCode(payload.code)) {
          setConnectError(cloudErrorToastMessage(payload, t));
        }
      }
    } finally {
      setSavingDefault(false);
    }
  };

  const handleContinue = () => {
    navigate(preserveUtmParams(getPostStoragePath(isNewUser)), { replace: true });
  };

  const handleSwitchAccount = async () => {
    try {
      await signOut(getAuth());
      navigate(preserveUtmParams('/auth/login'), { replace: true });
    } catch {
      navigate(preserveUtmParams('/auth/login'), { replace: true });
    }
  };

  return (
    <SketchPageShell className="min-h-screen flex flex-col items-center justify-center px-4">
      <SketchBox className="p-6 max-w-lg w-full">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[var(--color-text)] mb-2 scribble-underline inline-block">
            {t('storage.linkTitle', 'Link cloud providers')}
          </h2>
          {user?.email && (
            <p className="text-sm mb-2" style={{ color: 'var(--brand-from)' }}>
              {t('storage.enteredAs', { email: user.email, defaultValue: 'Signed in as {{email}}' })}
            </p>
          )}

          <SketchBox className="text-left p-4 mb-4 border border-emerald-500/40 bg-emerald-500/10">
            <p className="text-sm font-medium text-[var(--color-text)]">
              {t('storage.multiLinkHintTitle', 'Link clouds (safe)')}
            </p>
            <p className="text-xs mt-1 text-[var(--color-muted)]">
              {t(
                'storage.multiLinkHintBody',
                'Link Google Drive, Dropbox, OneDrive, or Yandex with one tap. This only connects your account for collaboration — it does not move any files.',
              )}
            </p>
            <p className="text-xs mt-2 text-[var(--color-muted)]">
              {t(
                'storage.collaborationHint',
                'To access shared files, link the same cloud as the track owner. Linking is quick and safe.',
              )}
            </p>
          </SketchBox>

          <SketchBox className="text-left p-4 mb-4 border border-amber-500/40 bg-amber-500/10">
            <p className="text-sm font-medium text-[var(--color-text)]">
              {t('storage.migrationSeparateTitle', 'Migration is separate')}
            </p>
            <p className="text-xs mt-1 text-[var(--color-muted)]">
              {t(
                'storage.migrationSeparateBody',
                'To move all existing project files to another cloud, use Transfer project storage in Settings. That is slow, pauses uploads, and can interrupt collaboration — do not confuse it with linking.',
              )}
            </p>
          </SketchBox>

          {(isExpiredReconnect || isFailedReconnect) && (
            <SketchBox className="text-left p-4 mb-4 border border-amber-500/40 bg-amber-500/10">
              <p className="text-sm font-medium">
                {isFailedReconnect
                  ? t('storage.failedTitle', 'Connection failed')
                  : t('storage.expiredTitle', 'Connection expired')}
              </p>
              <p className="text-xs mt-1 text-[var(--color-muted)]">
                {reconnectProvider
                  ? t('storage.reconnectProvider', {
                      provider: t(`storage.providers.${reconnectProvider}`, reconnectProvider),
                    })
                  : t('storage.expiredMessage', 'Reconnect your cloud to continue uploading.')}
              </p>
            </SketchBox>
          )}

          {connectError && (
            <SketchBox className="text-left p-4 mb-4 border border-red-500/40 bg-red-500/10">
              <p className="text-sm text-red-600">{connectError}</p>
            </SketchBox>
          )}

          <div className="mb-4 text-left">
            <StorageProviderGrid
              mode="onboarding"
              status={status}
              projectStorageProvider={defaultProvider || 'none'}
              eligibleForCassetteProjectStorage={eligibleForCassetteProjectStorage}
              busyProvider={busy}
              highlightedProvider={reconnectProvider}
              defaultProvider={defaultProvider}
              savingDefault={savingDefault}
              onConnect={handleConnect}
              onDisconnect={() => {}}
              onCassetteCloudUpsell={() => setShowCassettePaywall(true)}
              onSelectDefault={handleDefaultChange}
            />
          </div>

          <CassetteCloudPaywallModal
            isOpen={showCassettePaywall}
            onClose={() => setShowCassettePaywall(false)}
          />

          <div className="space-y-2">
            <SketchButton
              onClick={handleContinue}
              className="w-full"
              disabled={!canContinue}
              data-testid="continue-after-storage"
            >
              {t('storage.continue', 'Continue')}
            </SketchButton>
            <SketchButton onClick={handleSwitchAccount} className="w-full">
              {t('storage.switchAccount', 'Switch account')}
            </SketchButton>
          </div>
        </div>
      </SketchBox>
    </SketchPageShell>
  );
};

export default LinkStoragePage;
